import { useId, type ReactElement, type ReactNode } from 'react';
import { cloneElement } from 'react';

import { Label } from '@/shared/components/ui/label';

interface FormFieldProps {
  label: string;
  required?: boolean;
  /** Texto de apoio permanente — não some ao digitar, ao contrário do placeholder. */
  hint?: ReactNode;
  error?: string;
  children: ReactElement<{ id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }>;
}

/**
 * Rótulo + campo + ajuda + erro, amarrados por `id`.
 *
 * O `cloneElement` existe para que a tela escreva `<FormField label="Título">
 * <Input … /></FormField>` sem repetir três vezes o mesmo id — e para que
 * NENHUM campo do formulário fique sem `<label>` de verdade. Placeholder não
 * é rótulo: ele some justamente quando a pessoa precisa conferir o que estava
 * preenchendo (RNF-U01, voluntário sem treino).
 *
 * ⚠️ O filho precisa ser **um único controle de formulário**. Embrulhar dois
 * campos numa `div` faria o `id` cair na div, e o `<label>` passaria a
 * apontar para um elemento que não recebe foco — os dois campos ficariam sem
 * nome acessível. Para um par (data + hora), use `fieldset`/`legend` com
 * `aria-label` em cada campo, como em `SectionSchedule`.
 */
export function FormField({ label, required, hint, error, children }: FormFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-ajuda` : undefined;
  const errorId = error ? `${id}-erro` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={required}>
        {label}
      </Label>

      {cloneElement(children, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}

      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}

      {/* O erro DESCE sobre o campo (`reveal`), em vez de subir: ele veio de
          quem está logo acima — do campo que a pessoa acabou de deixar. */}
      {error ? (
        <p id={errorId} className="animate-reveal text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
